import { Link } from 'react-router-dom'

import { LanguageSwitch } from '../../i18n/LanguageSwitch'
import { useLocale } from '../../i18n/LocaleProvider'
import { LEGAL_POLICY_DOCUMENTS } from './legalPolicyContent'

function isSafeHref(href) {
  return /^(https?:\/\/|mailto:|\/)/.test(href)
}

function renderInlineMarkdown(text, keyPrefix) {
  const parts = []
  const pattern = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g
  let lastIndex = 0
  let match

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    if (match[1]) {
      parts.push(<strong key={`${keyPrefix}-strong-${match.index}`}>{match[1]}</strong>)
    } else {
      const label = match[2]
      const href = match[3]
      parts.push(
        isSafeHref(href) ? (
          <a
            key={`${keyPrefix}-link-${match.index}`}
            href={href}
            className="font-medium text-brand-600 hover:text-brand-700"
            rel={href.startsWith('http') ? 'noreferrer' : undefined}
          >
            {label}
          </a>
        ) : (
          label
        ),
      )
    }

    lastIndex = pattern.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts
}

function renderMarkdownContent(content) {
  const lines = content.split(/\r?\n/)
  const blocks = []
  let index = 0

  while (index < lines.length) {
    const line = lines[index]
    const trimmed = line.trim()

    if (!trimmed) {
      index += 1
      continue
    }

    if (/^-{3,}$/.test(trimmed)) {
      blocks.push(<hr key={`hr-${index}`} className="my-8 border-gray-200" />)
      index += 1
      continue
    }

    const heading = /^(#{1,3})\s+(.+)$/.exec(trimmed)
    if (heading) {
      const level = heading[1].length
      const Tag = level === 1 ? 'h2' : level === 2 ? 'h3' : 'h4'
      const className =
        level === 1
          ? 'mt-8 text-2xl font-bold text-gray-950 first:mt-0'
          : level === 2
            ? 'mt-7 text-xl font-semibold text-gray-900'
            : 'mt-6 text-base font-semibold text-gray-900'
      blocks.push(
        <Tag key={`heading-${index}`} className={className}>
          {renderInlineMarkdown(heading[2], `heading-${index}`)}
        </Tag>,
      )
      index += 1
      continue
    }

    if (/^\*\s+/.test(trimmed)) {
      const items = []
      while (index < lines.length && /^\*\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^\*\s+/, ''))
        index += 1
      }
      blocks.push(
        <ul key={`ul-${index}`} className="my-4 list-disc space-y-2 pl-6 text-gray-700">
          {items.map((item, itemIndex) => (
            <li key={`ul-${index}-${itemIndex}`}>
              {renderInlineMarkdown(item, `ul-${index}-${itemIndex}`)}
            </li>
          ))}
        </ul>,
      )
      continue
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items = []
      while (index < lines.length && /^\d+\.\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^\d+\.\s+/, ''))
        index += 1
      }
      blocks.push(
        <ol key={`ol-${index}`} className="my-4 list-decimal space-y-2 pl-6 text-gray-700">
          {items.map((item, itemIndex) => (
            <li key={`ol-${index}-${itemIndex}`}>
              {renderInlineMarkdown(item, `ol-${index}-${itemIndex}`)}
            </li>
          ))}
        </ol>,
      )
      continue
    }

    const paragraph = []
    while (
      index < lines.length &&
      lines[index].trim() &&
      !/^(#{1,3})\s+/.test(lines[index].trim()) &&
      !/^\*\s+/.test(lines[index].trim()) &&
      !/^\d+\.\s+/.test(lines[index].trim()) &&
      !/^-{3,}$/.test(lines[index].trim())
    ) {
      paragraph.push(lines[index].trim())
      index += 1
    }

    blocks.push(
      <p key={`p-${index}`} className="my-4 text-sm leading-7 text-gray-700">
        {renderInlineMarkdown(paragraph.join(' '), `p-${index}`)}
      </p>,
    )
  }

  return blocks
}

function LegalPolicyPage({ policy }) {
  const { locale } = useLocale()
  const config = LEGAL_POLICY_DOCUMENTS[policy]
  const document = config.documents[locale]

  return (
    <main className="min-h-screen bg-gray-50 text-gray-900">
      <section className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-4xl flex-col gap-6 px-4 py-8 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Link to="/" className="text-sm font-semibold text-brand-600 hover:text-brand-700">
              WorkflowPro
            </Link>
            <h1 className="mt-3 text-3xl font-bold text-gray-950">{config.label}</h1>
          </div>
          <LanguageSwitch />
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-10">
        <article className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
          {renderMarkdownContent(document.content)}
        </article>
      </section>
    </main>
  )
}

export function PrivacyPolicyPage() {
  return <LegalPolicyPage policy="privacy" />
}

export function TermsPolicyPage() {
  return <LegalPolicyPage policy="terms" />
}

export function CookiesPolicyPage() {
  return <LegalPolicyPage policy="cookies" />
}
